import { useEffect, useRef, useState } from 'react';
import { useSocket } from './useSocket';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { SocketEvents } from '../constants/socket-events';
import { DEFAULT_CHANNEL } from '../constants/channels';
import { createLogger } from '../utils/logger';
import { getActiveTenantId } from '../lib/tenantHeader';
import { roomChanges } from '../lib/tenantRooms';

const log = createLogger('SocketRooms');
/**
 * Keeps the socket in exactly the operator's tenant rooms (+ a global admin's current context
 * workspace): joins new ones and leaves ones no longer wanted.
 * A reconnect starts from nothing (the server forgot the rooms), so everything is re-joined.
 *
 * Note on operatorRoom: the gateway joins `operatorRoom(operatorId)` **automatically
 * on socket connect** (see messaging-gateway/CLAUDE.md "Socket.IO layer" — `On connect
 * it joins operatorRoom(operatorId) and emits OPERATOR_LOGGED_IN`). We don't need to
 * emit any JOIN_OPERATOR ourselves; this hook only needs to fan out tenantRoom joins.
 */
export function useSocketOperatorRooms() {
  const { socket, isConnected, emit } = useSocket();
  const { tenants } = useAuth();
  // A global admin's non-member tenant (the gateway admits and audits this join).
  const { contextTenant } = useTenant();

  // Rooms this socket connection is in — reset on disconnect or a new socket instance.
  const joined = useRef<{ socket: unknown; ids: Set<string> }>({ socket: null, ids: new Set() });

  useEffect(() => {
    if (!socket || !isConnected) {
      joined.current = { socket: null, ids: new Set() };
      return;
    }
    if (joined.current.socket !== socket) joined.current = { socket, ids: new Set() };
    const ids = (tenants ?? []).map((t) => t.id);
    if (contextTenant && !ids.includes(contextTenant.id)) ids.push(contextTenant.id);

    const { join, leave } = roomChanges(joined.current.ids, ids);
    leave.forEach((tenantId) => {
      emit(SocketEvents.LEAVE_TENANT, { tenantId });
      joined.current.ids.delete(tenantId);
      log.debug('Left tenantRoom:', `tenant:${tenantId}`);
    });
    join.forEach((tenantId) => {
      emit(SocketEvents.JOIN_TENANT, { tenantId });
      joined.current.ids.add(tenantId);
      log.debug('Joined tenantRoom:', `tenant:${tenantId}`);
    });
  }, [socket, isConnected, tenants, contextTenant, emit]);
}

/**
 * Hook to join pageRoom when a page is selected
 * @returns Whether the pageRoom is joined
 */
export function useSocketPageRoom(pageId: string | null, channel: string = DEFAULT_CHANNEL) {
  const { socket, isConnected, emit } = useSocket();
  const [pageRoomJoined, setPageRoomJoined] = useState(false);

  useEffect(() => {
    if (!socket || !isConnected || !pageId) {
      setPageRoomJoined(false);
      return;
    }

    // Listen for JOINED event to confirm room join
    const handleJoined = (data: any) => {
      if (data?.room === `page:${channel}:${pageId}`) {
        setPageRoomJoined(true);
        log.debug('Confirmed pageRoom join:', `page:${channel}:${pageId}`);
      }
    };

    socket.on(SocketEvents.JOINED, handleJoined);

    emit(SocketEvents.JOIN_PAGE, { pageId, channel, tenantId: getActiveTenantId() });
    log.debug('Joining pageRoom:', `page:${channel}:${pageId}`);

    return () => {
      socket.off(SocketEvents.JOINED, handleJoined);
      if (socket && pageId) {
        emit(SocketEvents.LEAVE_PAGE, { pageId, channel });
        log.debug('Left pageRoom:', `page:${channel}:${pageId}`);
        setPageRoomJoined(false);
      }
    };
  }, [socket, isConnected, pageId, channel, emit]);

  return pageRoomJoined;
}


